import { Send } from '@mui/icons-material';
import { useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, TextField, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { Formik, Form, Field, FieldProps } from 'formik';
import { sendHomeowersPortalFeedback } from '../../fetch';

interface IFeedbackValues {
  name: string;
  email: string;
  plan: string;
  subdivision: string;
  closingMonthAndYear: string;
  comments: string;
}

const FeedbackSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email().required('Required'),
  plan: Yup.string().required('Required'),
  subdivision: Yup.string().required('Required'),
  closingMonthAndYear: Yup.string().required('Required'),
  comments: Yup.string()
});

export const FeedbackSection = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const classes = useStyles();
  return (
    <Box
      padding={{
        xs: '0 1rem',
        md: '1rem 3rem'
      }}
    >
      <Box padding="2rem" sx={{ backgroundColor: '#edf5de' }}>
        {hasSubmitted && (
          <Typography variant="h5" align="center">
            Feedback Sent!
          </Typography>
        )}
        {!hasSubmitted && (
          <Formik
            enableReinitialize={true}
            initialValues={{
              name: '',
              email: '',
              plan: '',
              subdivision: '',
              closingMonthAndYear: '',
              comments: ''
            }}
            validationSchema={FeedbackSchema}
            onSubmit={async (values, actions) => {
              const res = await sendHomeowersPortalFeedback(values);

              if (res?.Detail) {
                enqueueSnackbar(res.Detail, {
                  variant: 'error'
                });
              } else {
                setHasSubmitted(true);
                actions.resetForm();
              }
            }}
          >
            {({ isSubmitting, isValid, dirty }) => {
              return (
                <Form>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Field name={`name`}>
                        {({ field, form }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField
                              {...field}
                              error={form?.touched?.name && form?.errors?.name ? true : false}
                              helperText={form?.touched?.name && form?.errors?.name}
                              className={classes.textfield}
                              fullWidth
                              variant="outlined"
                              label="First And Last"
                              autoComplete="nope"
                              required
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name={`email`}>
                        {({ field, form }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField
                              {...field}
                              error={form?.touched?.email && form?.errors?.email ? true : false}
                              helperText={form?.touched?.email && form?.errors?.email}
                              className={classes.textfield}
                              fullWidth
                              variant="outlined"
                              label="Email"
                              autoComplete="nope"
                              required
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name={`plan`}>
                        {({ field, form }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField
                              {...field}
                              error={form?.touched?.plan && form?.errors?.plan ? true : false}
                              helperText={form?.touched?.plan && form?.errors?.plan}
                              className={classes.textfield}
                              fullWidth
                              variant="outlined"
                              label="Plan You Built Or Purchased"
                              autoComplete="nope"
                              required
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name={`subdivision`}>
                        {({ field, form }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField
                              {...field}
                              error={form?.touched?.subdivision && form?.errors?.subdivision ? true : false}
                              helperText={form?.touched?.subdivision && form?.errors?.subdivision}
                              className={classes.textfield}
                              fullWidth
                              variant="outlined"
                              label="Subdivision You Moved Into"
                              autoComplete="nope"
                              required
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name={`closingMonthAndYear`}>
                        {({ field, form }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField
                              {...field}
                              error={form?.touched?.closingMonthAndYear && form?.errors?.closingMonthAndYear ? true : false}
                              helperText={form?.touched?.closingMonthAndYear && form?.errors?.closingMonthAndYear}
                              className={classes.textfield}
                              fullWidth
                              variant="outlined"
                              label="Month And Year You Closed"
                              autoComplete="nope"
                              required
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name={`comments`}>
                        {({ field }: FieldProps<IFeedbackValues>) => {
                          return (
                            <TextField {...field} multiline rows={3} className={classes.textfield} fullWidth variant="outlined" label="Your Testimonial" autoComplete="nope" />
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <LoadingButton
                        loading={isSubmitting}
                        loadingPosition="start"
                        variant="contained"
                        color="primary"
                        startIcon={<Send />}
                        type="submit"
                        disabled={!dirty || isSubmitting || !isValid}
                      >
                        Send
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        )}
      </Box>
      <Box marginTop="0.75rem">
        <Typography variant="caption">
          By clicking submit you are giving Stone Martin Builders permission to share your testimonial on our online channels and in all marketing materials.
        </Typography>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  textfield: {
    '&& .MuiOutlinedInput-root': {
      backgroundColor: '#fff'
    }
  }
}));
